
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select"
import { EmployeeStatus } from "@/contexts/employees/EmployeesContext"
import { StatusBadge } from "@/utils/statusUtils"

const statuses = [
  EmployeeStatus.ACTIVE,
  'On Leave', // Note: This is not in our enum, consider adding it
  EmployeeStatus.TERMINATED,
  EmployeeStatus.SUSPENDED
]

interface ChangeStatusProps {
  employee: {
    id: string
    name: string
    status?: string
  }
  onSave: (id: string, newStatus: string, reason: string) => void
  onCancel: () => void
}

export function ChangeStatus({ employee, onSave, onCancel }: ChangeStatusProps) {
  const currentStatus = employee.status || EmployeeStatus.ACTIVE
  const [newStatus, setNewStatus] = useState(currentStatus)
  
  const handleSubmit = () => {
    if (newStatus === currentStatus) {
      return
    }
    
    // Note: Reason field was intentionally removed from this simplified status change interface.
    // If audit trail requirements change, consider adding a reason input field back.
    // For now, passing empty string to maintain interface compatibility.
    onSave(employee.id, newStatus, "")
  }

  
  return (
    <div className="space-y-6">
      <div>
        <p className="text-muted-foreground">Update the status for {employee.name}</p>
      </div>
      
      <div className="space-y-4">
                 {/* Current Status and New Status in the same row */}
         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
           <div className="space-y-2">
             <Label>Current Status</Label>
             <div className="h-10 px-3 border rounded-md bg-muted/50 w-full flex items-center justify-center">
               <StatusBadge status={currentStatus} />
             </div>
           </div>
           
           <div className="space-y-2">
             <Label htmlFor="new-status">New Status</Label>
             <Select value={newStatus} onValueChange={setNewStatus}>
               <SelectTrigger className="h-10 w-full">
                 <SelectValue placeholder="Select status" />
               </SelectTrigger>
               <SelectContent>
                 {statuses.map(status => (
                   <SelectItem key={status} value={status}>{status}</SelectItem>
                 ))}
               </SelectContent>
             </Select>
           </div>
         </div>
      </div>
      
      <div className="flex justify-end gap-2 mt-6">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="secondary"
          disabled={newStatus === currentStatus}
        >
          Update Status
        </Button>
      </div>
    </div>
  )
}
